import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  GoogleAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signInWithPopup, sendPasswordResetEmail, onAuthStateChanged
} from "firebase/auth";
import { doc, getDoc, setDoc, getDocs, collection, query, where, orderBy } from "firebase/firestore";
import { auth, db } from "./firebase";
import { track } from "./analytics";
import Assessment from "./Assessment";
import { LegalLinks } from "./Legal";
import { PaymentHelp } from "./ClientApp";

const DEFAULT_COACH_BIO = "A passionate guide helping people discover their inner truth through the power of meditation, mindfulness, and conscious living. Elibariki brings warmth, depth, and personal experience to every program he creates.";

// Placeholder testimonials shown until the coach adds real ones in Admin → Catalog.
const DEFAULT_TESTIMONIALS = [
  { quote: "The daily sessions gave me a calm I hadn't felt in years. I finally understand my own mind.", name: "Amina H.", role: "Dar es Salaam" },
  { quote: "Coach Elibariki's guidance is warm and practical. Every session left me lighter and more focused.", name: "Joseph M.", role: "Arusha" },
  { quote: "I came for meditation and left with a whole new mindset. This program genuinely changed my life.", name: "Grace N.", role: "Mwanza" },
];

const initialsOf = (name = "") => name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "•";

const googleProvider = new GoogleAuthProvider();

const WHATSAPP_URL = "https://wa.me/255755633711";
const BOOK_SESSION_URL = `${WHATSAPP_URL}?text=${encodeURIComponent("Hi Coach Elibariki, I'd like to book a live 1-on-1 online coaching session. Could you please share availability and pricing?")}`;
const EMAIL_URL    = "mailto:elibarikimkumbo@gmail.com";
const INSTAGRAM_URL = "https://www.instagram.com/mkumboelibariki/";
const TIKTOK_URL    = "https://www.tiktok.com/@elibarikimkumbo";
const FACEBOOK_URL  = "https://www.facebook.com/elibariki.mkumbo";

const getOrCreateUserDoc = async (firebaseUser) => {
  const ref = doc(db, "users", firebaseUser.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      name: firebaseUser.displayName || "",
      email: firebaseUser.email,
      phone: firebaseUser.phoneNumber || "",
      paid: false,
      joinedAt: new Date().toISOString(),
      startDate: null,
      progress: {},
    });
    return { paid: false, isNew: true };
  }
  return { ...snap.data(), isNew: false };
};

// Phone signups reuse Firebase email/password auth under the hood via a synthetic
// identifier, so phone numbers cost nothing extra (no SMS / reCAPTCHA / console setup).
const PHONE_DOMAIN = "phone.beyondthebody.co.tz";
const phoneDigits  = (phone = "") => phone.replace(/\D/g, "");
const phoneToEmail = (phone = "") => `${phoneDigits(phone)}@${PHONE_DOMAIN}`;

const friendlyError = (code, method = "email") => {
  const byPhone = method === "phone";
  switch (code) {
    case "auth/email-already-in-use": return byPhone ? "Akaunti yenye namba hii ya simu ipo tayari. Jaribu kuingia. · An account with this phone number already exists — try signing in." : "Akaunti yenye barua pepe hii ipo tayari. Jaribu kuingia. · An account with this email already exists — try signing in.";
    case "auth/wrong-password":       return "Nenosiri si sahihi. Tafadhali jaribu tena. · Incorrect password, please try again.";
    case "auth/user-not-found":       return byPhone ? "Hakuna akaunti yenye namba hiyo ya simu. Jisajili badala yake? · No account found with that phone number — sign up instead?" : "Hakuna akaunti iliyopatikana. Jisajili badala yake? · No account found — sign up instead?";
    case "auth/invalid-email":        return byPhone ? "Tafadhali weka namba sahihi ya simu. · Please enter a valid phone number." : "Tafadhali weka barua pepe sahihi. · Please enter a valid email address.";
    case "auth/weak-password":        return "Nenosiri liwe na angalau herufi 6. · Password must be at least 6 characters.";
    case "auth/too-many-requests":    return "Umejaribu mara nyingi mno. Tafadhali subiri kidogo. · Too many attempts, please wait a moment.";
    case "auth/popup-closed-by-user": return "Kuingia kwa Google kumesitishwa. · Google sign-in was cancelled.";
    case "auth/popup-blocked":        return "Kivinjari chako kimezuia dirisha ibukizi. Ruhusu pop-ups kwa tovuti hii. · Popup was blocked — please allow popups for this site.";
    case "auth/invalid-credential":   return byPhone ? "Namba ya simu au nenosiri si sahihi. · Incorrect phone number or password." : "Barua pepe au nenosiri si sahihi. · Incorrect email or password.";
    default:                          return "Hitilafu imetokea. Tafadhali jaribu tena. · Something went wrong, please try again.";
  }
};

const EyeIcon = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>;
const EyeOffIcon = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;

// Password input with a show/hide eye toggle. Reused across login & signup.
function PasswordField({ value, onChange, placeholder, onKeyDown }) {
  const [show, setShow] = useState(false);
  return (
    <div className="pw-wrap">
      <input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} onKeyDown={onKeyDown} />
      <button type="button" className="pw-eye" onClick={() => setShow(s => !s)} aria-label={show ? "Hide password" : "Show password"} tabIndex={-1}>{show ? <EyeOffIcon /> : <EyeIcon />}</button>
    </div>
  );
}

export default function AuthPage({ skipLanding = false, initialScreen = "login", onBack }) {
  const [screen, setScreen]     = useState(() => skipLanding ? initialScreen : "landing");
  const [user, setUser]         = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        const data = await getOrCreateUserDoc(u);
        setUser(u);
        setUserData(data);
        if (data.paid) {
          setScreen("done");
        } else if (data.paymentPending) {
          setScreen("pending");
        } else {
          const assessSnap = await getDoc(doc(db, "assessments", u.uid));
          setScreen(assessSnap.exists() ? "payment" : "assessment");
        }
      } else {
        setUser(null);
        setUserData(null);
        setScreen(skipLanding ? initialScreen : "landing");
      }
    });
    return unsub;
  }, []);

  if (screen === "landing") {
    return <LandingPage onEnroll={() => setScreen("signup")} onEnrollMasterclass={() => setScreen("signup")} onSignIn={() => setScreen("login")} />;
  }

  const canGoBack = screen === "login" || screen === "signup" || screen === "reset";

  return (
    <div className="auth-page">
      <style>{CSS}</style>
      <div className="auth-container">
        {canGoBack && (
          <button className="back-home-btn" onClick={() => onBack ? onBack() : setScreen("landing")}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 3L5 8l5 5"/></svg>
            Back to home
          </button>
        )}
        <div className="auth-brand">
          <BrandLogo />
          <span>Beyond The Body</span>
        </div>
        {(screen === "login" || screen === "signup") && (
          <div className="auth-segmented" role="tablist">
            <button role="tab" className={`auth-seg-btn${screen === "signup" ? " active" : ""}`} onClick={() => setScreen("signup")}>
              Akaunti Mpya<span className="seg-en">Sign up</span>
            </button>
            <button role="tab" className={`auth-seg-btn${screen === "login" ? " active" : ""}`} onClick={() => setScreen("login")}>
              Nina Akaunti<span className="seg-en">Sign in</span>
            </button>
          </div>
        )}
        {screen === "login"      && <LoginForm  onSwitch={() => setScreen("signup")} onReset={() => setScreen("reset")} />}
        {screen === "signup"     && <SignupForm  onSwitch={() => setScreen("login")} />}
        {screen === "reset"      && <ResetForm   onBack={() => setScreen("login")} />}
        {screen === "assessment" && user && (
          <Assessment user={user} onPay={() => setScreen("payment")} onSkip={() => setScreen("payment")} />
        )}
        {screen === "payment" && user && <PaymentScreen user={user} userData={userData} />}
        {screen === "pending" && (
          <div className="auth-card" style={{textAlign:"center",padding:"40px 32px"}}>
            <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}>
              <svg viewBox="0 0 48 48" width="48" height="48" fill="none" stroke="#4F9CA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 6h32M8 42h32M10 6v8l14 10-14 10v8M38 6v8L24 24l14 10v8"/>
              </svg>
            </div>
            <h2 style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:"1.6rem",color:"#1F4E5A",marginBottom:8}}>Awaiting confirmation</h2>
            <p style={{color:"#5A7480",fontSize:".85rem",lineHeight:1.7,marginBottom:24}}>Your payment notification has been sent to the coach. You'll get full access as soon as it's confirmed — usually within a few minutes.</p>
            <button className="btn-signout-small" onClick={() => auth.signOut()}>Sign out</button>
          </div>
        )}
        {screen === "done" && (
          <div className="auth-card" style={{textAlign:"center",padding:"40px 32px"}}>
            <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}>
              <svg viewBox="0 0 48 48" width="48" height="48" fill="none" stroke="#3E7D6B" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="24" cy="24" r="20"/><polyline points="14,24 21,31 34,17"/>
              </svg>
            </div>
            <h2 style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:"1.6rem",color:"#1F4E5A",marginBottom:8}}>Payment confirmed!</h2>
            <p style={{color:"#5A7480",fontSize:"0.85rem"}}>Reloading your program…</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LANDING PAGE ─────────────────────────────────────────────────────────────

export function LandingPage({ onEnroll, onEnrollMasterclass, onSignIn, user, onGoToApp }) {
  const [programs, setPrograms] = useState([]);
  const [masterclasses, setMasterclasses] = useState([]);
  const [catalogConfig, setCatalogConfig] = useState({});

  useEffect(() => {
    getDocs(query(collection(db, "programs"), where("published", "==", true), orderBy("order")))
      .then(snap => setPrograms(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.archived !== true)))
      .catch(() =>
        getDocs(query(collection(db, "programs"), where("published", "==", true)))
          .then(snap => setPrograms(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.archived !== true)))
      );
    getDocs(query(collection(db, "masterclasses"), where("published", "==", true)))
      .then(snap => setMasterclasses(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(m => !m.eventDate || new Date(m.eventDate).getTime() > Date.now())
        .sort((a, b) => new Date(a.eventDate || 0) - new Date(b.eventDate || 0))))
      .catch(() => {});
    getDoc(doc(db, "config", "catalog"))
      .then(snap => { if (snap.exists()) setCatalogConfig(snap.data()); })
      .catch(() => {});
  }, []);

  const coachName  = catalogConfig.coachName   || "Elibariki Mkumbo";
  const coachRole  = catalogConfig.coachRole   || "Meditation Coach & Founder";
  const coachBio   = catalogConfig.coachOverview || DEFAULT_COACH_BIO;
  // Swahili-primary by default; if a coach sets custom copy in config/catalog we
  // render it verbatim (no auto English sub-line) so admin overrides stay intact.
  const heroTitleCustom   = !!catalogConfig.heroTitle;
  const heroTaglineCustom = !!catalogConfig.heroTagline;
  const heroTitle  = catalogConfig.heroTitle   || "Kuwa Vile Ulivyokusudiwa Kuwa.";
  const heroTitleEn  = "Become Who You're Meant to Be.";
  const heroTaglineEn= "Your Journey. Your Truth. Your Transformation.";
  const heroTagline= catalogConfig.heroTagline || "Safari Yako. Ukweli Wako. Mabadiliko Yako.";
  const heroDesc   = catalogConfig.heroDesc    || "Beyond The Body inakuletea programu za kubadilisha maisha kupitia kutafakari, nishati, mtazamo, na ukuaji binafsi — ukiongozwa na Kocha Elibariki Mkumbo. (Transformative programs in meditation, energy work, mindset & personal growth.)";
  const aboutLead  = catalogConfig.aboutLead  || "Beyond The Body is a platform for complete life transformation — covering meditation, energy, mindset, purpose, and personal growth. Guided by Coach Elibariki, every program is designed to help you move beyond your limitations and become who you're truly meant to be.";

  const reduceMotion = useReducedMotion();
  const showHeroPortrait = !catalogConfig.heroImageUrl && !!catalogConfig.coachImageUrl;
  const testimonials = (catalogConfig.testimonials && catalogConfig.testimonials.length)
    ? catalogConfig.testimonials : DEFAULT_TESTIMONIALS;
  const rise = (i = 0) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 16 },
    whileInView: reduceMotion ? undefined : { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.5, ease: "easeOut", delay: i * 0.06 },
  });

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Sticky mobile CTA — appears once the hero scrolls out of view so the main
  // action is always one tap away on phones (where program links land).
  const [showStickyCta, setShowStickyCta] = useState(false);
  useEffect(() => {
    const hero = document.getElementById("hero");
    if (!hero || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { rootMargin: "-72px 0px 0px 0px" }
    );
    io.observe(hero);
    return () => io.disconnect();
  }, []);

  return (
    <div className="lp">
      <style>{CSS}</style>
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <a href="#hero" className="lp-logo" onClick={e => { e.preventDefault(); scrollTo("hero"); }}>
            <BrandLogo color="#1F4E5A" size={34} />
            <span>Beyond The Body</span>
          </a>
          <div className="lp-nav-links">
            <button onClick={() => scrollTo("about")}>About</button>
            <button onClick={() => scrollTo("how")}>How It Works</button>
            <button onClick={() => scrollTo("programs")}>Programs</button>
            <button onClick={() => scrollTo("work")}>Sessions</button>
            <button onClick={() => scrollTo("testimonials")}>Stories</button>
            <button onClick={() => scrollTo("masterclasses")}>Masterclasses</button>
            <button onClick={() => scrollTo("contact")}>Contact</button>
          </div>
          <div className="lp-nav-actions">
            {user ? (
              <button className="lp-enroll-btn" onClick={onGoToApp}>Programs →</button>
            ) : (
              <>
                <button className="lp-signin-btn lp-nav-programs" onClick={onGoToApp}>Programs →</button>
                <button className="lp-signin-btn" onClick={onSignIn}>Ingia</button>
                <button className="lp-enroll-btn" onClick={onEnroll}>Jisajili</button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section id="hero" className={`lp-hero${catalogConfig.heroImageUrl ? ' lp-hero--image' : ''}`}
        style={catalogConfig.heroImageUrl ? {
          backgroundImage: `linear-gradient(135deg, rgba(20,50,58,0.86) 0%, rgba(31,78,90,0.6) 55%, rgba(255,122,77,0.45) 100%), url(${catalogConfig.heroImageUrl})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
        } : undefined}>
        <div className={`lp-hero-inner${showHeroPortrait ? ' has-portrait' : ''}`}>
          <div className="lp-hero-content">
            <p className="lp-hero-eyebrow">Online Courses & Coaching</p>
            <h1 className="lp-hero-title">{heroTitle}</h1>
            {!heroTitleCustom && <p className="lp-hero-title-en">{heroTitleEn}</p>}
            <p className="lp-hero-tagline">{heroTagline}{!heroTaglineCustom && <span className="lp-hero-tagline-en">{heroTaglineEn}</span>}</p>
            <p className="lp-hero-desc">{heroDesc}</p>
            <div className="lp-hero-ctas">
              {user ? (
                <>
                  <button className="lp-cta-primary" onClick={onGoToApp}>Programs →</button>
                  <button className="lp-cta-ghost" onClick={() => scrollTo("programs")}>Browse programs below</button>
                </>
              ) : (
                <>
                  <button className="lp-cta-primary" onClick={() => scrollTo("programs")}>Anza Safari Yako →</button>
                  <button className="lp-cta-ghost" onClick={onSignIn}>Tayari ni mwanachama? Ingia · Already a member? Sign in</button>
                </>
              )}
            </div>
          </div>
          {showHeroPortrait && (
            <motion.div className="lp-hero-portrait"
              initial={reduceMotion ? false : { opacity: 0, y: 16 }}
              animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}>
              <img src={catalogConfig.coachImageUrl} alt={coachName} />
            </motion.div>
          )}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="lp-section lp-about">
        <div className="lp-section-inner">
          <p className="lp-section-eyebrow">About Us</p>
          <h2 className="lp-section-title">Why Beyond The Body?</h2>
          <p className="lp-section-lead">{aboutLead}</p>
          <div className="lp-benefits">
            {[
              {
                icon: <svg viewBox="0 0 40 40" width="36" height="36" fill="none" stroke="#4F9CA8" strokeWidth="1.8" strokeLinecap="round"><circle cx="20" cy="20" r="3"/><line x1="20" y1="4" x2="20" y2="10"/><line x1="20" y1="30" x2="20" y2="36"/><line x1="4" y1="20" x2="10" y2="20"/><line x1="30" y1="20" x2="36" y2="20"/><line x1="8.3" y1="8.3" x2="12.5" y2="12.5"/><line x1="27.5" y1="27.5" x2="31.7" y2="31.7"/><line x1="31.7" y1="8.3" x2="27.5" y2="12.5"/><line x1="12.5" y1="27.5" x2="8.3" y2="31.7"/></svg>,
                title: catalogConfig.benefit1Title || "Discover Your Purpose",
                desc: catalogConfig.benefit1Desc || "Uncover what truly drives you and align your life with your deepest values.",
              },
              {
                icon: <svg viewBox="0 0 40 40" width="36" height="36" fill="none" stroke="#4F9CA8" strokeWidth="1.8" strokeLinecap="round"><path d="M20 36c-6-4-12-10-12-18a12 12 0 0124 0c0 8-6 14-12 18z"/><path d="M20 14v8M16 18h8"/></svg>,
                title: catalogConfig.benefit2Title || "Transform Your Mind",
                desc: catalogConfig.benefit2Desc || "Break limiting beliefs and rewire your thinking through guided daily practice.",
              },
              {
                icon: <svg viewBox="0 0 40 40" width="36" height="36" fill="none" stroke="#4F9CA8" strokeWidth="1.8" strokeLinecap="round"><path d="M20 8v4M12 12l3 3M28 12l-3 3M8 20h4M28 20h4M14 28l-3 3M26 28l3 3M20 28v4"/><circle cx="20" cy="20" r="6"/></svg>,
                title: catalogConfig.benefit3Title || "Elevate Your Energy",
                desc: catalogConfig.benefit3Desc || "Cultivate inner energy and vitality that radiates into every area of your life.",
              },
              {
                icon: <svg viewBox="0 0 40 40" width="36" height="36" fill="none" stroke="#4F9CA8" strokeWidth="1.8" strokeLinecap="round"><path d="M6 32l14-20 14 20"/><path d="M14 32l6-8 6 8"/><path d="M20 12V8"/><circle cx="20" cy="6" r="2" fill="#4F9CA8"/></svg>,
                title: catalogConfig.benefit4Title || "Live Beyond Limitations",
                desc: catalogConfig.benefit4Desc || "Step into a life of freedom, confidence, and unlimited possibility.",
              },
            ].map(({ icon, title, desc }, i) => (
              <motion.div key={title} className="lp-benefit-card" {...rise(i)}>
                <div className="lp-benefit-icon">{icon}</div>
                <h3 className="lp-benefit-title">{title}</h3>
                <p className="lp-benefit-desc">{desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="lp-coach">
            <div className="lp-coach-avatar">
              {catalogConfig.coachImageUrl ? (
                <img src={catalogConfig.coachImageUrl} alt={coachName} style={{width:"100%",height:"100%",objectFit:"cover"}} />
              ) : (
                <svg viewBox="0 0 80 80" width="80" height="80" fill="none">
                  <circle cx="40" cy="40" r="40" fill="#1F4E5A"/>
                  <circle cx="40" cy="30" r="13" fill="#9FCAD2" opacity="0.9"/>
                  <path d="M12 72c0-15.464 12.536-28 28-28s28 12.536 28 28" fill="#9FCAD2" opacity="0.7"/>
                </svg>
              )}
            </div>
            <div className="lp-coach-info">
              <p className="lp-coach-name">{coachName}</p>
              <p className="lp-coach-role">{coachRole}</p>
              <p className="lp-coach-bio">{coachBio}</p>
              <div className="lp-coach-socials">
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><InstagramIcon /></a>
                <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" aria-label="TikTok"><TikTokIcon /></a>
                <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FacebookIcon /></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="lp-section lp-how">
        <div className="lp-section-inner">
          <p className="lp-section-eyebrow">The Process</p>
          <h2 className="lp-section-title">How It Works</h2>
          <p className="lp-section-lead">Four simple steps to begin your transformation.</p>
          <div className="lp-how-grid">
            {[
              { num: "01", title: "Choose Your Program", desc: "Browse programs designed for your goals — meditation, mindset, energy, or personal growth. Find what speaks to you." },
              { num: "02", title: "Create Your Free Account", desc: "Sign up in seconds. Complete a short wellness assessment so Coach Elibariki can personalise your journey." },
              { num: "03", title: "Enroll & Begin", desc: "Unlock your program with a one-time AzamPay mobile-money payment and start your guided daily sessions right away." },
              { num: "04", title: "Transform Your Life", desc: "Work through sessions, journal your reflections, and experience real, lasting change at your own pace." },
            ].map((s, i) => (
              <motion.div key={s.num} className="lp-step" {...rise(i)}>
                <div className="lp-step-badge">{s.num}</div>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-desc">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section id="programs" className="lp-section lp-programs">
        <div className="lp-section-inner">
          <p className="lp-section-eyebrow">Our Programs</p>
          <h2 className="lp-section-title">Choose Your Journey</h2>
          <p className="lp-section-lead">Every program is a step toward becoming who you're meant to be.</p>
          <div className="lp-programs-grid">
            {programs.length === 0 ? (
              [0, 1, 2].map(i => (
                <div key={i} className="lp-prog-card" style={{ cursor: "default" }} aria-hidden="true">
                  <div className="skel lp-prog-img" />
                  <div className="lp-prog-body">
                    <div className="skel" style={{ height: 19, width: "70%", marginBottom: 12 }} />
                    <div className="skel" style={{ height: 13, width: "92%", marginBottom: 7 }} />
                    <div className="skel" style={{ height: 13, width: "55%", marginBottom: 20 }} />
                    <div className="lp-prog-footer">
                      <div className="skel" style={{ height: 16, width: 64 }} />
                      <div className="skel" style={{ height: 34, width: 84, borderRadius: 8 }} />
                    </div>
                  </div>
                </div>
              ))
            ) : programs.map(p => (
              <div key={p.id} className="lp-prog-card" role="button" tabIndex={0}
                onClick={() => onEnroll(p)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onEnroll(p); } }}>
                {p.coverImageUrl
                  ? <img src={p.coverImageUrl} alt={p.title} className="lp-prog-img" />
                  : <div className="lp-prog-img-placeholder"><BrandLogo color="#9FCAD2" size={40} /></div>
                }
                <div className="lp-prog-body">
                  <h3 className="lp-prog-title">{p.title}</h3>
                  {p.tagline && <p className="lp-prog-tagline">{p.tagline}</p>}
                  <div className="lp-prog-footer">
                    <span className="lp-prog-price">{p.price ? `TZS ${Number(p.price).toLocaleString()}` : "Free"}</span>
                    <button className="lp-prog-enroll" onClick={e => { e.stopPropagation(); onEnroll(p); }}>Enroll →</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="lp-programs-note">
            Payments processed securely via AzamPay mobile money.
            Full instructions provided after sign-up.
          </p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="lp-section lp-testimonials">
        <div className="lp-section-inner">
          <p className="lp-section-eyebrow">Real Stories</p>
          <h2 className="lp-section-title">What Our Clients Say</h2>
          <p className="lp-section-lead">Transformation you can feel — in their own words.</p>
          <div className="lp-testi-grid">
            {testimonials.map((t, i) => (
              <motion.figure key={(t.name || "") + i} className="lp-testi-card" {...rise(i)}>
                <svg className="lp-testi-mark" viewBox="0 0 32 24" width="34" height="26" aria-hidden="true">
                  <path d="M0 24V12C0 5 4 1 11 0l1 4C8 5 6 7 6 11h5v13H0zm21 0V12c0-7 4-11 11-12l1 4c-4 1-6 3-6 7h5v13H21z" fill="currentColor"/>
                </svg>
                <blockquote className="lp-testi-quote">{t.quote}</blockquote>
                <figcaption className="lp-testi-author">
                  <span className="lp-testi-avatar">
                    {t.photoUrl ? <img src={t.photoUrl} alt={t.name || ""} /> : <span>{initialsOf(t.name)}</span>}
                  </span>
                  <span className="lp-testi-meta">
                    <span className="lp-testi-name">{t.name}</span>
                    {t.role && <span className="lp-testi-role">{t.role}</span>}
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* WORK WITH COACH — dual path */}
      <section id="work" className="lp-section lp-work">
        <div className="lp-section-inner">
          <p className="lp-section-eyebrow">Work With Elibariki</p>
          <h2 className="lp-section-title">Two Ways to Begin</h2>
          <p className="lp-section-lead">Go at your own pace, or get personal one-on-one guidance — whichever fits you best.</p>
          <div className="lp-work-grid">
            <motion.div className="lp-work-card" {...rise(0)}>
              <div className="lp-work-icon">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l9 5-9 5-9-5 9-5z"/><path d="M3 13l9 5 9-5"/></svg>
              </div>
              <h3 className="lp-work-title">Self-Paced Programs</h3>
              <p className="lp-work-desc">Guided daily sessions you follow on your own schedule — meditation, mindset, energy and growth.</p>
              <ul className="lp-work-points">
                {["Guided audio & video sessions", "Daily reflection journal", "Lifetime access to your program"].map(pt => (
                  <li key={pt}><svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3,8.5 6.5,12 13,4"/></svg>{pt}</li>
                ))}
              </ul>
              <button className="lp-cta-secondary" onClick={() => scrollTo("programs")}>Browse programs</button>
            </motion.div>
            <motion.div className="lp-work-card lp-work-card--live" {...rise(1)}>
              <span className="lp-work-badge">1-on-1</span>
              <div className="lp-work-icon">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="13" height="12" rx="2"/><path d="M22 8l-5 4 5 4V8z"/></svg>
              </div>
              <h3 className="lp-work-title">Live Online Session</h3>
              <p className="lp-work-desc">Talk directly with Coach Elibariki in a private video or phone session, focused entirely on you.</p>
              <ul className="lp-work-points">
                {["Personal one-on-one coaching", "Video or phone, your choice", "Flexible scheduling"].map(pt => (
                  <li key={pt}><svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3,8.5 6.5,12 13,4"/></svg>{pt}</li>
                ))}
              </ul>
              <a className="lp-cta-primary" href={BOOK_SESSION_URL} target="_blank" rel="noopener noreferrer">Book a session →</a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MASTERCLASSES */}
      {masterclasses.length > 0 && (
        <section id="masterclasses" className="lp-section lp-masterclasses">
          <div className="lp-section-inner">
            <p className="lp-section-eyebrow">Live & Interactive</p>
            <h2 className="lp-section-title">Upcoming Masterclasses</h2>
            <p className="lp-section-lead">Join Coach Elibariki live. Reserve your spot — limited seats.</p>
            <div className="lp-mc-list">
              {masterclasses.map(mc => {
                const d = mc.eventDate ? new Date(mc.eventDate) : null;
                const go = () => (onEnrollMasterclass ? onEnrollMasterclass(mc) : onEnroll());
                return (
                  <div key={mc.id} className="lp-mc-card" role="button" tabIndex={0}
                    onClick={go} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}>
                    <div className="lp-mc-media">
                      {mc.coverImageUrl
                        ? <img src={mc.coverImageUrl} alt={mc.title} />
                        : <div className="lp-mc-media-ph" />}
                      {d && <span className="lp-mc-datechip">{d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · {d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>}
                    </div>
                    <div className="lp-mc-body">
                      <h3 className="lp-mc-title">{mc.title}</h3>
                      {mc.description && <p className="lp-mc-desc">{mc.description}</p>}
                      <div className="lp-mc-footer">
                        <span className="lp-mc-price">{mc.price ? `TZS ${Number(mc.price).toLocaleString()}` : "Free"}</span>
                        <button className="lp-mc-enroll" onClick={e => { e.stopPropagation(); go(); }}>Enroll →</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="lp-section lp-contact">
        <div className="lp-section-inner">
          <p className="lp-section-eyebrow lp-eyebrow-light">Wasiliana nasi</p>
          <h2 className="lp-section-title lp-title-light">We'd Love to Hear From You</h2>
          <p className="lp-section-lead lp-lead-light">Questions about the program? Reach us on any channel.</p>
          <div className="lp-contact-links">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="lp-contact-item lp-contact-whatsapp">
              <WhatsAppIcon />
              <div>
                <p className="lp-contact-label">WhatsApp</p>
                <p className="lp-contact-value">+255 755 633 711</p>
              </div>
            </a>
            <a href={EMAIL_URL} className="lp-contact-item lp-contact-email">
              <EmailIcon />
              <div>
                <p className="lp-contact-label">Email</p>
                <p className="lp-contact-value">elibarikimkumbo@gmail.com</p>
              </div>
            </a>
          </div>
          <div className="lp-social-links">
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="lp-social-btn">
              <InstagramIcon /> Instagram
            </a>
            <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" className="lp-social-btn">
              <TikTokIcon /> TikTok
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="lp-social-btn">
              <FacebookIcon /> Facebook
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <BrandLogo color="#1F4E5A" size={24} />
            <span>Beyond The Body</span>
          </div>
          <p className="lp-footer-copy">© {new Date().getFullYear()} Beyond The Body · beyondthebody.co.tz</p>
          <p className="lp-footer-copy" style={{ marginTop: 6 }}><LegalLinks /></p>
          <div className="lp-footer-links">
            <button onClick={() => scrollTo("about")}>About</button>
            <button onClick={() => scrollTo("how")}>How It Works</button>
            <button onClick={() => scrollTo("programs")}>Programs</button>
            <button onClick={() => scrollTo("contact")}>Contact</button>
            {user ? (
              <button onClick={onGoToApp}>My Programs</button>
            ) : (
              <button onClick={onSignIn}>Sign In</button>
            )}
          </div>
        </div>
      </footer>

      {/* STICKY MOBILE CTA — phones only, after hero scrolls away */}
      <div className={`lp-sticky-cta${showStickyCta ? " is-visible" : ""}`}>
        {user ? (
          <button className="lp-sticky-primary" onClick={onGoToApp}>Go to My Programs →</button>
        ) : (
          <button className="lp-sticky-primary" onClick={() => scrollTo("programs")}>Start Your Journey →</button>
        )}
        <a className="lp-sticky-wa" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp">
          <WhatsAppIcon />
        </a>
      </div>
    </div>
  );
}

// ─── AUTH FORMS ───────────────────────────────────────────────────────────────

function LoginForm({ onSwitch, onReset }) {
  const [method, setMethod] = useState("email");
  const [email, setEmail] = useState(""); const [phone, setPhone] = useState("+255"); const [password, setPassword] = useState("");
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const handleLogin  = async () => {
    setError(""); setLoading(true);
    try {
      localStorage.setItem("btb_session","btb_v2");
      const loginId = method === "phone" ? phoneToEmail(phone) : email.trim();
      await signInWithEmailAndPassword(auth, loginId, password);
    } catch(e) { setError(friendlyError(e.code, method)); }
    setLoading(false);
  };
  const handleGoogle = async () => { setError(""); setLoading(true); try { await signInWithPopup(auth, googleProvider); } catch(e) { setError(friendlyError(e.code)); setLoading(false); } };
  const canSubmit = password && (method === "phone" ? phoneDigits(phone).length >= 9 : email.trim());
  return (
    <div className="auth-card">
      <h1 className="auth-title">Karibu tena</h1>
      <p className="auth-title-en">Welcome back · Ingia kuendelea na safari yako</p>
      <button className="btn-google" onClick={handleGoogle} disabled={loading}><GoogleIcon />Endelea na Google <span className="label-en">Continue with Google</span></button>
      <div className="auth-divider"><span>au · or</span></div>
      {error && <div className="auth-error">{error}</div>}
      <div className="auth-tabs">
        <button className={`auth-tab${method==="email"?" active":""}`} onClick={()=>{setMethod("email");setError("");}}>Barua pepe</button>
        <button className={`auth-tab${method==="phone"?" active":""}`} onClick={()=>{setMethod("phone");setError("");}}>Simu</button>
      </div>
      {method === "email"
        ? <div className="field"><label>Barua pepe <span className="label-en">Email</span></label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" onKeyDown={e=>e.key==="Enter"&&handleLogin()} /></div>
        : <div className="field"><label>Namba ya simu <span className="label-en">Phone</span></label><input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+255712345678" onKeyDown={e=>e.key==="Enter"&&handleLogin()} /></div>}
      <div className="field"><label style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}><span>Nenosiri <span className="label-en">Password</span></span> {method==="email" && <button className="link-btn" onClick={onReset}>Umesahau? · Forgot?</button>}</label><PasswordField value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&handleLogin()} /></div>
      <button className="btn-primary" onClick={handleLogin} disabled={loading||!canSubmit}>{loading ? <span className="btn-spinner"/> : <span>Ingia <span className="label-en" style={{color:"rgba(255,255,255,.8)"}}>Sign in</span></span>}</button>
      <p className="auth-switch">Huna akaunti? · Don't have an account? <button className="link-btn" onClick={onSwitch}>Jisajili bure</button></p>
    </div>
  );
}

function SignupForm({ onSwitch }) {
  const [method, setMethod] = useState("email");
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [confirm, setConfirm] = useState(""); const [phone, setPhone] = useState("+255");
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const mismatch = confirm.length > 0 && password !== confirm;
  const handleSignup = async () => {
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match. Please re-enter them."); return; }
    if (method === "phone" && phoneDigits(phone).length < 9) { setError("Please enter a valid phone number."); return; }
    setLoading(true);
    try {
      localStorage.setItem("btb_session","btb_v2");
      const signupId = method === "phone" ? phoneToEmail(phone) : email.trim();
      const cred = await createUserWithEmailAndPassword(auth, signupId, password);
      await setDoc(doc(db,"users",cred.user.uid),{ name, email: method === "phone" ? "" : email.trim(), phone, authMethod: method, paid:false, joinedAt:new Date().toISOString(), startDate:null, progress:{} });
      track("sign_up", { method });
    } catch(e) { setError(friendlyError(e.code, method)); }
    setLoading(false);
  };
  const handleGoogle = async () => { setError(""); setLoading(true); try { await signInWithPopup(auth, googleProvider); } catch(e) { setError(friendlyError(e.code)); setLoading(false); } };
  const canSubmit = name && password && confirm && (method === "phone" ? phoneDigits(phone).length >= 9 : email.trim());
  return (
    <div className="auth-card">
      <h1 className="auth-title">Fungua akaunti yako bila malipo</h1>
      <p className="auth-title-en">Create your free account · Anza safari yako leo</p>
      <button className="btn-google" onClick={handleGoogle} disabled={loading}><GoogleIcon />Endelea na Google <span className="label-en">Continue with Google</span></button>
      <div className="auth-divider"><span>au · or</span></div>
      {error && <div className="auth-error">{error}</div>}
      <div className="auth-tabs">
        <button className={`auth-tab${method==="email"?" active":""}`} onClick={()=>{setMethod("email");setError("");}}>Barua pepe</button>
        <button className={`auth-tab${method==="phone"?" active":""}`} onClick={()=>{setMethod("phone");setError("");}}>Simu</button>
      </div>
      <div className="field"><label>Jina kamili <span className="label-en">Full name</span></label><input value={name} onChange={e=>setName(e.target.value)} placeholder="Amina Hassan" /></div>
      {method === "email"
        ? <>
            <div className="field"><label>Barua pepe <span className="label-en">Email</span></label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" /></div>
            <div className="field"><label>Namba ya simu ya WhatsApp <span className="label-en">WhatsApp number</span></label><input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+255712345678" /></div>
          </>
        : <div className="field"><label>Namba ya simu ya WhatsApp <span className="label-en">WhatsApp number</span></label><input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+255712345678" /></div>}
      <div className="field"><label>Nenosiri <span className="label-en">Password</span></label><PasswordField value={password} onChange={e=>setPassword(e.target.value)} placeholder="Angalau herufi 6 · At least 6 characters" /></div>
      <div className="field"><label>Thibitisha nenosiri <span className="label-en">Confirm password</span></label><PasswordField value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Rudia nenosiri lako" onKeyDown={e=>e.key==="Enter"&&handleSignup()} />{mismatch && <p className="field-hint err">Nenosiri halilingani · Passwords don't match</p>}</div>
      <button className="btn-primary" onClick={handleSignup} disabled={loading||!canSubmit}>{loading ? <span className="btn-spinner"/> : <span>Jisajili <span className="label-en" style={{color:"rgba(255,255,255,.8)"}}>Create account</span></span>}</button>
      <p className="auth-switch">Tayari una akaunti? · Already have an account? <button className="link-btn" onClick={onSwitch}>Ingia</button></p>
    </div>
  );
}

function ResetForm({ onBack }) {
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false); const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const handle = async () => { setError(""); setLoading(true); try { await sendPasswordResetEmail(auth,email); setSent(true); } catch(e) { setError(friendlyError(e.code)); } setLoading(false); };
  return (
    <div className="auth-card">
      <h1 className="auth-title">Weka upya nenosiri</h1>
      <p className="auth-title-en">Reset password · Tutakutumia kiungo kwenye barua pepe yako</p>
      {sent ? <div className="auth-success"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:"-3px",marginRight:6}}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>Kiungo kimetumwa kwa <strong>{email}</strong> · Reset link sent<br/><button className="btn-primary" style={{marginTop:16}} onClick={onBack}>Rudi kuingia · Back to Sign In</button></div> : <>
        {error && <div className="auth-error">{error}</div>}
        <div className="field"><label>Barua pepe <span className="label-en">Email</span></label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" onKeyDown={e=>e.key==="Enter"&&handle()} /></div>
        <button className="btn-primary" onClick={handle} disabled={loading||!email}>{loading ? <span className="btn-spinner"/> : <span>Tuma kiungo <span className="label-en" style={{color:"rgba(255,255,255,.8)"}}>Send reset link</span></span>}</button>
        <p className="auth-switch"><button className="link-btn" onClick={onBack}>← Rudi kuingia · Back to Sign In</button></p>
      </>}
    </div>
  );
}

function PaymentScreen({ user, userData }) {
  const [loading, setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [phone, setPhone]       = useState(userData?.phone || "+255");

  // Reached the payment screen — record it (funnel: catch people who land here but
  // never confirm) and fire the GA4 checkout event. Runs once on open.
  useEffect(() => {
    if (!user) return;
    track("begin_checkout", {});
    setDoc(doc(db, "users", user.uid), { reachedPaymentAt: new Date().toISOString() }, { merge: true }).catch(() => {});
  }, [user]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, "users", user.uid), { paymentPending: true, phone: phone.trim() || userData?.phone || "" }, { merge: true });
      track("payment_confirmed", {});
      user.getIdToken().then(token =>
        fetch("/.netlify/functions/notify-payment", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({
            userName: userData?.name || user.displayName || "Client",
            email: user.email,
            phone: userData?.phone || "—",
          }),
        })
      ).catch(e => console.error("Notification failed:", e));
      setSubmitted(true);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="auth-card" style={{textAlign:"center",padding:"40px 32px"}}>
        <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}>
          <svg viewBox="0 0 48 48" width="48" height="48" fill="none" stroke="#4F9CA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6h32M8 42h32M10 6v8l14 10-14 10v8M38 6v8L24 24l14 10v8"/></svg>
        </div>
        <h2 style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:"1.6rem",color:"#1F4E5A",marginBottom:8}}>Notification sent!</h2>
        <p style={{color:"#5A7480",fontSize:".85rem",lineHeight:1.7}}>The coach has been notified. You'll get access once your payment is confirmed.</p>
      </div>
    );
  }

  return (
    <div className="auth-card">
      <h1 className="auth-title">Unlock Your Program</h1>
      <p className="auth-sub">One-time payment · Full 10-day access</p>
      <div className="program-summary">
        {[
          [<svg key="cal" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1.5" y="3" width="13" height="12" rx="1.5"/><path d="M5 1.5v3M11 1.5v3M1.5 7h13"/></svg>,"10-day guided meditation program"],
          [<svg key="aud" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9a5 5 0 0110 0"/><rect x="1" y="9" width="3" height="4" rx="1"/><rect x="12" y="9" width="3" height="4" rx="1"/></svg>,"33 audio sessions across 10 days"],
          [<svg key="jnl" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="1" width="10" height="14" rx="1"/><path d="M5 5h6M5 8h6M5 11h4"/></svg>,"Daily reflection journal"],
          [<svg key="lck" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2.5" y="7" width="11" height="8" rx="1.5"/><path d="M5 7V5a3 3 0 016 0v2"/></svg>,"Lifetime access to your sessions"],
        ].map(([icon,text])=>(
          <div key={String(text)} className="ps-item"><span style={{display:"flex",alignItems:"center"}}>{icon}</span><span>{text}</span></div>
        ))}
      </div>
      <div className="price-tag">
        <span className="price-amount">TZS 5,000</span>
        <span className="price-note">one-time · all inclusive</span>
      </div>
      {!userData?.phone && (
        <div className="field" style={{marginBottom:16}}>
          <label>Your Phone Number</label>
          <input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+255712345678" />
        </div>
      )}
      <button className="btn-primary" onClick={handleConfirm} disabled={loading}>
        {loading ? <span className="btn-spinner"/> : "Notify Coach of Payment"}
      </button>
      <PaymentHelp />
      <button className="btn-signout-small" onClick={() => auth.signOut()}>Sign out</button>
    </div>
  );
}

// ─── SHARED ICONS ─────────────────────────────────────────────────────────────

function BrandLogo({ color = "#9FCAD2", size = 42 }) {
  return (
    <svg viewBox="0 0 44 44" width={size} height={size} fill="none">
      <circle cx="22" cy="22" r="20" stroke={color} strokeWidth="1.5"/>
      <circle cx="22" cy="11.5" r="2.5" fill={color}/>
      <path d="M19.5 16L12 9" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M24.5 16L32 9" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="22" y1="14" x2="22" y2="27" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="22" y1="27" x2="15" y2="35" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="22" y1="27" x2="29" y2="35" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function GoogleIcon() {
  return <svg viewBox="0 0 24 24" width="18" height="18" style={{flexShrink:0}}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84z"/>
  </svg>;
}

function WhatsAppIcon() {
  return <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>;
}

function EmailIcon() {
  return <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="M2 7l10 7 10-7"/>
  </svg>;
}

function InstagramIcon() {
  return <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
  </svg>;
}

function TikTokIcon() {
  return <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.77a4.85 4.85 0 01-1.01-.08z"/>
  </svg>;
}

function FacebookIcon() {
  return <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>;
}

// small inline icon helpers for programs section
const calIcon = <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1.5" y="3" width="13" height="12" rx="1.5"/><path d="M5 1.5v3M11 1.5v3M1.5 7h13"/></svg>;
const headphonesIcon = <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9a5 5 0 0110 0"/><rect x="1" y="9" width="3" height="4" rx="1"/><rect x="12" y="9" width="3" height="4" rx="1"/></svg>;
const journalIcon = <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="1" width="10" height="14" rx="1"/><path d="M5 5h6M5 8h6M5 11h4"/></svg>;
const lockIcon = <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2.5" y="7" width="11" height="8" rx="1.5"/><path d="M5 7V5a3 3 0 016 0v2"/></svg>;
const coachIcon = <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6"/></svg>;

// ─── STYLES ───────────────────────────────────────────────────────────────────

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --cream:#F2F6F7;--parchment:#E3EEF0;--bark:#4F9CA8;--soil:#1F4E5A;--moss:#3E7D6B;
  --dawn:#9FCAD2;--stone:#8FA6AD;--text:#1E2E33;--lt:#5A7480;--border:#D6E2E5;
  --navy:#14323A;--navy2:#173E48;--gold:#4F9CA8;--gold2:#3E7D8A;
  --accent:#FF7A4D;--accent2:#FFB020;
  --energy:#F5A314;--energy-deep:#DB8E0A;--energy-ink:#1F4E5A;--energy-text:#9A6207;
}
body{font-family:'Inter',sans-serif;color:var(--text)}

/* ── LANDING PAGE ── */
.lp{min-height:100vh;background:var(--cream)}

/* nav */
.lp-nav{position:sticky;top:0;z-index:100;background:rgba(247,243,237,0.97);backdrop-filter:blur(12px);border-bottom:1px solid var(--border)}
.lp-nav-inner{max-width:1100px;margin:0 auto;padding:0 24px;height:64px;display:flex;align-items:center;gap:24px}
.lp-logo{display:flex;align-items:center;gap:10px;font-family:'Plus Jakarta Sans',sans-serif;font-size:1.25rem;font-weight:600;color:var(--soil);text-decoration:none;flex-shrink:0}
.lp-nav-links{display:flex;gap:4px;flex:1;justify-content:center}
.lp-nav-links button{background:none;border:none;color:var(--lt);font-family:'Inter',sans-serif;font-size:.88rem;padding:8px 14px;border-radius:8px;cursor:pointer;transition:all .2s}
.lp-nav-links button:hover{color:var(--soil);background:var(--parchment)}
.lp-nav-actions{display:flex;align-items:center;gap:10px;flex-shrink:0}
.lp-signin-btn{background:none;border:1px solid var(--border);color:var(--bark);font-family:'Inter',sans-serif;font-size:.85rem;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .2s}
.lp-signin-btn:hover{border-color:var(--bark);background:var(--parchment)}
.lp-enroll-btn{background:var(--soil);border:none;color:white;font-family:'Inter',sans-serif;font-size:.85rem;font-weight:600;padding:9px 18px;border-radius:8px;cursor:pointer;transition:all .2s}
.lp-enroll-btn:hover{background:var(--bark)}
@media(max-width:640px){.lp-nav-links,.lp-nav-programs{display:none}}

/* hero */
.lp-hero{min-height:560px;display:flex;align-items:center;justify-content:center;position:relative;padding:88px 24px;background:linear-gradient(145deg,#EAF4F5 0%,#D6ECEE 60%,#FBE7C7 115%);border-bottom:1px solid var(--border);overflow:hidden}
.lp-hero-inner{max-width:1080px;width:100%;display:flex;align-items:center;justify-content:center;gap:56px;position:relative;z-index:1}
.lp-hero-inner.has-portrait{justify-content:space-between}
.lp-hero-inner.has-portrait .lp-hero-content{text-align:left;margin:0;flex:1}
.lp-hero-inner.has-portrait .lp-hero-desc{margin-left:0;margin-right:0}
.lp-hero-inner.has-portrait .lp-hero-ctas{align-items:flex-start}
.lp-hero-portrait{flex-shrink:0;width:380px;max-width:42vw}
.lp-hero-portrait img{width:100%;aspect-ratio:4/5;object-fit:cover;border-radius:24px;box-shadow:0 24px 60px rgba(20,50,58,.22);border:4px solid #fff;outline:2px solid var(--energy);outline-offset:-2px;display:block}
@media(max-width:768px){.lp-hero-inner{flex-direction:column-reverse;gap:36px}.lp-hero-inner.has-portrait .lp-hero-content{text-align:center}.lp-hero-inner.has-portrait .lp-hero-desc{margin-left:auto;margin-right:auto}.lp-hero-inner.has-portrait .lp-hero-ctas{align-items:center}.lp-hero-portrait{width:240px;max-width:70vw}}
.lp-hero-content{max-width:660px;text-align:center;position:relative;z-index:1;animation:fadeUp .7s ease both}
.lp-hero-eyebrow{font-size:.78rem;letter-spacing:.2em;text-transform:uppercase;color:var(--energy-text);margin-bottom:20px;font-weight:700}
.lp-hero-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:clamp(2.7rem,7.5vw,4.8rem);font-weight:700;color:var(--soil);line-height:1.05;margin-bottom:16px;letter-spacing:-0.02em}
.lp-hero-title-en{font-family:'Inter',sans-serif;font-size:clamp(.95rem,2.2vw,1.2rem);font-weight:400;color:var(--lt);margin:-6px 0 18px;line-height:1.3}
.lp-hero-tagline{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.2rem;font-style:italic;color:var(--bark);margin-bottom:20px}
.lp-hero-tagline-en{display:block;font-style:normal;font-family:'Inter',sans-serif;font-size:.82rem;font-weight:400;color:var(--lt);margin-top:3px}
.lp-hero-desc{font-size:.98rem;color:var(--lt);line-height:1.75;margin-bottom:36px;max-width:520px;margin-left:auto;margin-right:auto}
.lp-hero-ctas{display:flex;flex-direction:column;align-items:center;gap:14px}
.lp-hero-trust{margin-top:26px}
.lp-hero-loc{font-size:.82rem;font-weight:600;color:var(--soil);letter-spacing:.02em;margin-bottom:12px}
.lp-hero-points{list-style:none;display:flex;flex-wrap:wrap;gap:8px 20px;padding:0;margin:0}
.lp-hero-points li{display:flex;align-items:center;gap:7px;font-size:.84rem;color:var(--lt)}
.lp-hero-points li svg{color:var(--moss);flex-shrink:0}
.lp-hero-inner.has-portrait .lp-hero-points{justify-content:flex-start}
@media(max-width:768px){.lp-hero-points{justify-content:center}.lp-hero-inner.has-portrait .lp-hero-points{justify-content:center}}
.lp-hero--image .lp-hero-loc{color:#fff}
.lp-hero--image .lp-hero-points li{color:rgba(255,255,255,.85)}
.lp-hero--image .lp-hero-points li svg{color:var(--dawn)}
.lp-cta-primary{background:var(--energy);color:var(--energy-ink);border:none;font-family:'Inter',sans-serif;font-size:1.02rem;font-weight:700;padding:16px 40px;border-radius:12px;cursor:pointer;transition:transform .2s,box-shadow .2s,background .2s;box-shadow:0 8px 22px rgba(245,163,20,.32)}
.lp-cta-primary:hover{transform:translateY(-2px);background:var(--energy-deep);box-shadow:0 12px 30px rgba(245,163,20,.42)}
/* hero with background image — light text */
.lp-hero--image .lp-hero-title{color:#fff}
.lp-hero--image .lp-hero-title-en{color:rgba(255,255,255,.82)}
.lp-hero--image .lp-hero-tagline{color:rgba(255,255,255,.92)}
.lp-hero--image .lp-hero-tagline-en{color:rgba(255,255,255,.78)}
.lp-hero--image .lp-hero-desc{color:rgba(255,255,255,.82)}
.lp-hero--image .lp-hero-eyebrow{color:#FFD9C7}
.lp-hero--image .lp-cta-ghost{color:rgba(255,255,255,.85)}
.lp-hero--image .lp-cta-ghost:hover{color:#fff}
.lp-cta-ghost{background:none;border:none;color:var(--lt);font-family:'Inter',sans-serif;font-size:.85rem;cursor:pointer;transition:color .2s;text-decoration:underline;text-underline-offset:3px}
.lp-cta-ghost:hover{color:var(--soil)}

/* sticky mobile CTA */
.lp-sticky-cta{position:fixed;left:0;right:0;bottom:0;z-index:1200;display:none;gap:10px;padding:10px 14px calc(10px + env(safe-area-inset-bottom));background:rgba(255,255,255,.96);backdrop-filter:blur(8px);border-top:1px solid var(--border);box-shadow:0 -4px 20px rgba(20,50,58,.1);transform:translateY(110%);transition:transform .28s ease}
.lp-sticky-cta.is-visible{transform:translateY(0)}
.lp-sticky-primary{flex:1;background:var(--energy);color:var(--energy-ink);border:none;font-family:'Inter',sans-serif;font-size:1rem;font-weight:700;padding:14px 0;border-radius:12px;cursor:pointer;box-shadow:0 6px 16px rgba(245,163,20,.3)}
.lp-sticky-wa{flex:0 0 52px;display:flex;align-items:center;justify-content:center;background:#25D366;color:#fff;border-radius:12px;text-decoration:none}
.lp-sticky-wa svg{width:24px;height:24px}
@media(max-width:768px){.lp-sticky-cta{display:flex}}

/* sections */
.lp-section{padding:60px 24px}
.lp-section-inner{max-width:1000px;margin:0 auto}
.lp-section-eyebrow{font-size:.75rem;letter-spacing:.2em;text-transform:uppercase;color:var(--energy-text);font-weight:700;margin-bottom:12px}
.lp-eyebrow-light{color:var(--dawn)}
.lp-section-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:clamp(2rem,5vw,3rem);font-weight:600;color:var(--soil);margin-bottom:16px;line-height:1.15}
.lp-title-light{color:white}
.lp-section-lead{font-size:.95rem;color:var(--lt);line-height:1.75;max-width:600px;margin-bottom:32px}
.lp-lead-light{color:rgba(255,255,255,0.7)}

/* about */
.lp-about{background:white}
.lp-benefits{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:24px;margin-bottom:40px}
.lp-benefit-card{background:var(--cream);border:1px solid var(--border);border-radius:16px;padding:28px 24px;transition:transform .2s,box-shadow .2s}
.lp-benefit-card:hover{transform:translateY(-4px);box-shadow:0 12px 32px rgba(20,50,58,.1)}
.lp-benefit-icon{margin-bottom:16px}
.lp-benefit-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.15rem;font-weight:600;color:var(--soil);margin-bottom:8px}
.lp-benefit-desc{font-size:.82rem;color:var(--lt);line-height:1.65}
.lp-coach{display:flex;align-items:flex-start;gap:24px;background:var(--cream);border:1px solid var(--border);border-radius:20px;padding:32px;flex-wrap:wrap}
.lp-coach-avatar{flex-shrink:0;width:88px;height:88px;border-radius:50%;overflow:hidden;border:2px solid var(--dawn)}
.lp-coach-info{flex:1;min-width:200px}
.lp-coach-name{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.3rem;font-weight:600;color:var(--soil);margin-bottom:2px}
.lp-coach-role{font-size:.78rem;color:var(--bark);letter-spacing:.08em;text-transform:uppercase;margin-bottom:10px}
.lp-coach-bio{font-size:.85rem;color:var(--lt);line-height:1.7;margin-bottom:16px}
.lp-coach-socials{display:flex;gap:12px}
.lp-coach-socials a{display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:8px;border:1px solid var(--border);color:var(--bark);transition:all .2s;text-decoration:none}
.lp-coach-socials a:hover{border-color:var(--bark);color:var(--soil);background:var(--parchment)}

/* programs grid */
.lp-programs{background:white}
.lp-programs-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:24px;margin-bottom:32px}
.lp-prog-card{background:white;border:1px solid var(--border);border-radius:16px;overflow:hidden;transition:transform .2s,box-shadow .2s;cursor:pointer}
.lp-prog-card:focus-visible{outline:2px solid var(--bark);outline-offset:2px}
.lp-prog-card:hover{transform:translateY(-4px);box-shadow:0 16px 40px rgba(20,50,58,.12)}
.lp-prog-img{width:100%;aspect-ratio:16/10;object-fit:cover;display:block}
.lp-prog-img-placeholder{width:100%;aspect-ratio:16/9;background:var(--parchment);display:flex;align-items:center;justify-content:center}
.lp-prog-body{padding:20px}
.lp-prog-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.2rem;font-weight:600;color:var(--soil);margin-bottom:4px}
.lp-prog-tagline{font-size:.8rem;color:var(--lt);line-height:1.5;margin-bottom:16px}
.lp-prog-footer{display:flex;align-items:center;justify-content:space-between}
.lp-prog-price{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.1rem;font-weight:700;color:var(--soil)}
.lp-prog-enroll{background:var(--energy);color:var(--energy-ink);border:none;font-family:'Inter',sans-serif;font-size:.82rem;font-weight:700;padding:9px 18px;border-radius:8px;cursor:pointer;transition:transform .2s,box-shadow .2s,background .2s}
.lp-prog-enroll:hover{background:var(--energy-deep)}
.lp-programs-note{font-size:.8rem;color:var(--stone);text-align:center;line-height:1.7}
.lp-programs-note a{color:var(--bark);text-decoration:underline;text-underline-offset:3px}

/* masterclasses */
.lp-masterclasses{background:white}
.lp-mc-list{display:flex;flex-direction:column;gap:18px}
.lp-mc-card{display:flex;background:var(--cream);border:1px solid var(--border);border-radius:16px;overflow:hidden;cursor:pointer;transition:transform .2s,box-shadow .2s;text-align:left}
.lp-mc-card:hover{transform:translateY(-3px);box-shadow:0 16px 40px rgba(20,50,58,.12)}
.lp-mc-card:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.lp-mc-media{position:relative;width:210px;flex-shrink:0}
.lp-mc-media img{width:100%;height:100%;object-fit:cover;display:block}
.lp-mc-media-ph{width:100%;height:100%;min-height:160px;background:linear-gradient(135deg,#2E6B5E,#1F4E5A)}
.lp-mc-datechip{position:absolute;top:12px;left:12px;background:var(--energy);color:var(--energy-ink);font-size:.72rem;font-weight:700;padding:4px 10px;border-radius:20px;letter-spacing:.03em;box-shadow:0 4px 12px rgba(245,163,20,.4)}
.lp-mc-body{padding:18px 20px;flex:1;min-width:0;display:flex;flex-direction:column}
.lp-mc-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.2rem;font-weight:600;color:var(--soil);margin-bottom:6px}
.lp-mc-desc{font-size:.85rem;color:var(--lt);line-height:1.6;margin-bottom:14px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.lp-mc-footer{display:flex;align-items:center;justify-content:space-between;margin-top:auto}
.lp-mc-price{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.05rem;font-weight:600;color:var(--bark)}
.lp-mc-enroll{background:var(--energy);color:var(--energy-ink);border:none;font-family:'Inter',sans-serif;font-size:.82rem;font-weight:700;padding:9px 18px;border-radius:8px;cursor:pointer;transition:background .2s,transform .2s}
.lp-mc-enroll:hover{background:var(--energy-deep);transform:translateY(-1px)}
@media(max-width:560px){.lp-mc-card{flex-direction:column}.lp-mc-media{width:100%;height:170px}}
/* dynamic image zoom on hover */
.lp-prog-img{transition:transform .4s ease}
.lp-prog-card:hover .lp-prog-img{transform:scale(1.05)}
.lp-mc-media img{transition:transform .4s ease}
.lp-mc-card:hover .lp-mc-media img{transform:scale(1.05)}

/* contact */
.lp-contact{background:var(--soil)}
.lp-contact-links{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px;margin-bottom:32px}
.lp-contact-item{display:flex;align-items:center;gap:16px;padding:20px 24px;border-radius:14px;text-decoration:none;transition:all .2s}
.lp-contact-whatsapp{background:rgba(255,255,255,0.1);border:1px solid rgba(255,255,255,0.2);color:white}
.lp-contact-whatsapp:hover{background:rgba(255,255,255,0.18);transform:translateY(-2px)}
.lp-contact-email{background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.15);color:white}
.lp-contact-email:hover{background:rgba(255,255,255,0.15);transform:translateY(-2px)}
.lp-contact-label{font-size:.72rem;letter-spacing:.1em;text-transform:uppercase;opacity:.6;margin-bottom:3px}
.lp-contact-value{font-size:.9rem;font-weight:500}
.lp-social-links{display:flex;flex-wrap:wrap;gap:12px;justify-content:center}
.lp-social-btn{display:inline-flex;align-items:center;gap:8px;padding:11px 20px;border-radius:10px;background:rgba(255,255,255,0.1);border:1px solid rgba(255,255,255,0.2);color:rgba(255,255,255,0.85);font-family:'Inter',sans-serif;font-size:.85rem;text-decoration:none;transition:all .2s}
.lp-social-btn:hover{background:rgba(255,255,255,0.2);color:white;transform:translateY(-2px)}

/* footer */
.lp-footer{background:var(--text);padding:32px 24px}
.lp-footer-inner{max-width:1000px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:16px;text-align:center}
.lp-footer-brand{display:flex;align-items:center;gap:10px;font-family:'Plus Jakarta Sans',sans-serif;font-size:1.1rem;color:var(--dawn)}
.lp-footer-copy{font-size:.75rem;color:rgba(255,255,255,0.3)}
.lp-footer-links{display:flex;gap:8px;flex-wrap:wrap;justify-content:center}
.lp-footer-links button{background:none;border:none;color:rgba(255,255,255,0.4);font-family:'Inter',sans-serif;font-size:.78rem;cursor:pointer;padding:4px 10px;border-radius:6px;transition:color .2s}
.lp-footer-links button:hover{color:rgba(255,255,255,0.8)}

/* testimonials */
.lp-testimonials{background:linear-gradient(180deg,#FFFDF7 0%,#FBF1DC 100%)}
.lp-testi-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:22px;margin-bottom:8px}
.lp-testi-card{display:flex;flex-direction:column;background:#fff;border:1px solid var(--border);border-radius:18px;padding:28px 26px;margin:0;box-shadow:0 10px 30px rgba(20,50,58,.07);transition:transform .2s,box-shadow .2s}
.lp-testi-card:hover{transform:translateY(-4px);box-shadow:0 18px 44px rgba(20,50,58,.12)}
.lp-testi-mark{color:var(--energy);margin-bottom:14px;flex-shrink:0}
.lp-testi-quote{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.02rem;font-style:italic;color:var(--soil);line-height:1.6;margin:0 0 22px}
.lp-testi-author{display:flex;align-items:center;gap:13px;margin-top:auto}
.lp-testi-avatar{flex-shrink:0;width:46px;height:46px;border-radius:50%;overflow:hidden;background:var(--soil);display:flex;align-items:center;justify-content:center}
.lp-testi-avatar img{width:100%;height:100%;object-fit:cover}
.lp-testi-avatar span{font-family:'Plus Jakarta Sans',sans-serif;font-size:.92rem;font-weight:700;color:var(--dawn);letter-spacing:.02em}
.lp-testi-meta{display:flex;flex-direction:column;min-width:0}
.lp-testi-name{font-family:'Plus Jakarta Sans',sans-serif;font-size:.95rem;font-weight:600;color:var(--text)}
.lp-testi-role{font-size:.78rem;color:var(--bark)}

/* work with coach (dual path) */
.lp-work{background:var(--cream)}
.lp-work-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:24px;margin-top:8px}
.lp-work-card{display:flex;flex-direction:column;background:#fff;border:1px solid var(--border);border-radius:20px;padding:32px 28px;transition:transform .2s,box-shadow .2s}
.lp-work-card:hover{transform:translateY(-4px);box-shadow:0 16px 40px rgba(20,50,58,.1)}
.lp-work-card--live{background:linear-gradient(160deg,#FFFDF7 0%,#FBF1DC 100%);border-color:#F2D9A6;position:relative}
.lp-work-badge{position:absolute;top:20px;right:20px;background:var(--energy);color:var(--energy-ink);font-size:.68rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:4px 11px;border-radius:20px}
.lp-work-icon{width:52px;height:52px;border-radius:14px;display:flex;align-items:center;justify-content:center;background:#fff;border:1px solid var(--border);color:var(--bark);margin-bottom:18px}
.lp-work-card--live .lp-work-icon{color:var(--energy-text);border-color:#F2D9A6}
.lp-work-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.3rem;font-weight:600;color:var(--soil);margin-bottom:8px}
.lp-work-desc{font-size:.85rem;color:var(--lt);line-height:1.65;margin-bottom:16px}
.lp-work-points{list-style:none;padding:0;margin:0 0 24px;display:flex;flex-direction:column;gap:9px}
.lp-work-points li{display:flex;align-items:center;gap:9px;font-size:.85rem;color:var(--text)}
.lp-work-points li svg{color:var(--moss);flex-shrink:0}
.lp-work-card .lp-cta-primary,.lp-work-card .lp-cta-secondary{margin-top:auto;width:100%;text-align:center;text-decoration:none;display:inline-block}
.lp-cta-secondary{background:transparent;color:var(--soil);border:1.5px solid var(--soil);font-family:'Inter',sans-serif;font-size:1rem;font-weight:600;padding:14px 28px;border-radius:12px;cursor:pointer;transition:background .2s,color .2s}
.lp-cta-secondary:hover{background:var(--soil);color:#fff}
@media(max-width:680px){.lp-work-grid{grid-template-columns:1fr}}

/* how it works */
.lp-how{background:var(--cream)}
.lp-how-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}
.lp-step{position:relative;text-align:center}
.lp-step-badge{position:relative;z-index:1;width:66px;height:66px;margin:0 auto 18px;border-radius:50%;background:#fff;border:2px solid var(--energy);display:flex;align-items:center;justify-content:center;font-family:'Plus Jakarta Sans',sans-serif;font-size:1.45rem;font-weight:700;color:var(--energy-text);box-shadow:0 6px 18px rgba(245,163,20,.18)}
.lp-step:not(:last-child)::after{content:'';position:absolute;top:33px;left:50%;right:-50%;height:2px;background:linear-gradient(90deg,var(--energy) 0%,var(--dawn) 100%);z-index:0}
.lp-step-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.1rem;font-weight:600;color:var(--soil);margin-bottom:8px}
.lp-step-desc{font-size:.82rem;color:var(--lt);line-height:1.65;padding:0 6px}
@media(max-width:720px){.lp-how-grid{grid-template-columns:1fr;gap:30px;max-width:340px;margin:0 auto}.lp-step:not(:last-child)::after{display:none}}

/* ── AUTH FORMS ── */
.auth-page{min-height:100vh;display:flex;align-items:center;justify-content:center;background:linear-gradient(180deg,#F2F6F7 0%,#E3EEF0 100%);position:relative;padding:24px}
.auth-container{width:100%;max-width:420px;display:flex;flex-direction:column;align-items:center;gap:20px;position:relative;z-index:1}
.auth-brand{display:flex;align-items:center;gap:12px;font-family:'Plus Jakarta Sans',sans-serif;font-size:1.5rem;font-weight:600;color:var(--soil);letter-spacing:.02em;animation:fadeUp .6s ease both}
@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
.back-home-btn{display:flex;align-items:center;gap:6px;background:rgba(255,255,255,0.5);border:1px solid var(--border);color:var(--lt);font-family:'Inter',sans-serif;font-size:.78rem;padding:7px 14px;border-radius:8px;cursor:pointer;transition:all .2s;backdrop-filter:blur(4px)}
.back-home-btn:hover{background:rgba(255,255,255,0.75);color:var(--text)}
.auth-card{background:var(--cream);border-radius:20px;padding:32px;width:100%;box-shadow:0 20px 60px rgba(20,50,58,.2);animation:fadeUp .7s ease .1s both}
.auth-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.7rem;font-weight:600;color:var(--soil);margin-bottom:4px;line-height:1.2}
.auth-sub{font-size:.83rem;color:var(--lt);margin-bottom:24px;font-weight:300}
.btn-google{width:100%;display:flex;align-items:center;justify-content:center;gap:10px;padding:11px;border:1px solid var(--border);border-radius:10px;background:white;color:var(--text);cursor:pointer;font-family:'Inter',sans-serif;font-size:.88rem;font-weight:500;transition:all .2s;margin-bottom:16px}
.btn-google:hover{border-color:var(--bark);background:var(--parchment)}.btn-google:disabled{opacity:.6;cursor:not-allowed}
.auth-divider{display:flex;align-items:center;gap:12px;margin-bottom:16px;font-size:.72rem;color:var(--stone)}
.auth-divider::before,.auth-divider::after{content:'';flex:1;height:1px;background:var(--border)}
.auth-error{background:#FBF0EE;border:1px solid #E8C4BE;color:#8C3A2E;font-size:.8rem;padding:10px 12px;border-radius:8px;margin-bottom:14px;line-height:1.5}
.auth-success{background:#EDF5EB;border:1px solid #C4D4BF;color:#3E7D6B;font-size:.85rem;padding:16px;border-radius:10px;line-height:1.6}
.field{margin-bottom:14px}
.field label{display:block;font-size:.75rem;font-weight:600;color:var(--bark);margin-bottom:6px;text-transform:uppercase;letter-spacing:.06em}
.field input{width:100%;padding:11px 13px;border:1px solid var(--border);border-radius:9px;font-family:'Inter',sans-serif;font-size:.9rem;background:white;color:var(--text);transition:border-color .15s}
.field input:focus{outline:none;border-color:var(--bark)}
.field input::placeholder{color:var(--stone)}
.pw-wrap{position:relative}
.pw-wrap input{width:100%;padding:11px 44px 11px 13px;border:1px solid var(--border);border-radius:9px;font-family:'Inter',sans-serif;font-size:.9rem;background:white;color:var(--text);transition:border-color .15s}
.pw-wrap input:focus{outline:none;border-color:var(--bark)}
.pw-wrap input::placeholder{color:var(--stone)}
.pw-eye{position:absolute;right:6px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--stone);padding:6px;display:flex;align-items:center;border-radius:6px}
.pw-eye:hover{color:var(--bark)}
.field-hint{font-size:.72rem;margin-top:5px;color:var(--stone)}
.field-hint.err{color:#8C3A2E}
.auth-tabs{display:flex;gap:4px;background:var(--parchment);padding:4px;border-radius:10px;margin-bottom:16px}
.auth-tab{flex:1;padding:9px;border:none;background:none;border-radius:7px;font-family:'Inter',sans-serif;font-size:.82rem;font-weight:600;color:var(--lt);cursor:pointer;transition:all .15s}
.auth-tab.active{background:white;color:var(--soil);box-shadow:0 1px 3px rgba(20,50,58,.12)}
.auth-tab:hover:not(.active){color:var(--bark)}
.auth-segmented{display:flex;gap:6px;background:var(--parchment);padding:6px;border-radius:14px;margin-bottom:20px}
.auth-seg-btn{flex:1;display:flex;flex-direction:column;align-items:center;gap:1px;padding:11px 8px;border:none;background:none;border-radius:10px;font-family:'Plus Jakarta Sans',sans-serif;font-size:.98rem;font-weight:600;color:var(--lt);cursor:pointer;transition:all .15s;line-height:1.15}
.auth-seg-btn .seg-en{font-family:'Inter',sans-serif;font-size:.66rem;font-weight:400;letter-spacing:.02em;opacity:.75}
.auth-seg-btn.active{background:white;color:var(--soil);box-shadow:0 2px 8px rgba(20,50,58,.14)}
.auth-seg-btn:hover:not(.active){color:var(--bark)}
.label-en{font-weight:400;color:var(--lt);text-transform:none;letter-spacing:0;font-size:.68rem;margin-left:6px}
.auth-title-en{font-family:'Inter',sans-serif;font-size:.82rem;font-weight:400;color:var(--lt);margin:2px 0 22px;line-height:1.4}
.btn-primary{width:100%;padding:13px;margin-top:6px;background:var(--soil);color:white;border:none;border-radius:10px;font-family:'Inter',sans-serif;font-size:.9rem;font-weight:500;cursor:pointer;transition:all .2s;display:flex;align-items:center;justify-content:center;gap:8px}
.btn-primary:hover:not(:disabled){background:var(--bark);transform:translateY(-1px)}.btn-primary:disabled{opacity:.5;cursor:not-allowed;transform:none}
.btn-spinner{width:16px;height:16px;border:2px solid rgba(255,255,255,.3);border-top-color:white;border-radius:50%;animation:spin .7s linear infinite;display:inline-block}
@keyframes spin{to{transform:rotate(360deg)}}
.link-btn{background:none;border:none;cursor:pointer;color:var(--bark);font-family:'Inter',sans-serif;font-size:inherit;text-decoration:underline;text-underline-offset:3px}
.link-btn:hover{color:var(--soil)}
.auth-switch{font-size:.82rem;color:var(--lt);text-align:center;margin-top:16px}
.program-summary{background:white;border:1px solid var(--border);border-radius:12px;padding:14px 16px;display:flex;flex-direction:column;gap:8px;margin-bottom:16px}
.ps-item{display:flex;align-items:center;gap:10px;font-size:.82rem;color:var(--text)}
.price-tag{display:flex;flex-direction:column;align-items:center;padding:16px;background:var(--parchment);border-radius:12px;margin-bottom:20px}
.price-amount{font-family:'Plus Jakarta Sans',sans-serif;font-size:2rem;font-weight:600;color:var(--soil)}
.price-note{font-size:.72rem;color:var(--stone);margin-top:2px}
.btn-signout-small{display:block;width:100%;margin-top:14px;background:none;border:1px solid var(--border);color:var(--stone);border-radius:8px;padding:8px;font-family:'Inter',sans-serif;font-size:.75rem;cursor:pointer;transition:all .15s}
.btn-signout-small:hover{border-color:var(--stone);color:var(--lt)}
.mpesa-box{background:#E6F2F0;border:1px solid #C4D4BF;border-radius:12px;padding:18px 20px;margin-bottom:20px;text-align:center}
.mpesa-step{font-size:.8rem;color:var(--lt);margin-bottom:8px;line-height:1.5}
.mpesa-number{display:block;font-family:'Plus Jakarta Sans',sans-serif;font-size:1.6rem;font-weight:600;color:var(--soil);letter-spacing:.02em;text-decoration:none;transition:color .2s}
.mpesa-number:hover{color:var(--bark)}
.mpesa-name{font-size:.85rem;color:var(--bark);font-weight:500;margin-top:2px}
`;
